/**
 * SceneEditor - Main page component for editing scenes
 * 
 * This is the main editor interface that combines:
 * - SceneCanvas for visual editing
 * - TimelinePanel for timeline/signal editing
 * - PropertiesPanel for element properties
 * - Toolbar for actions
 *
 * Standalone (`/scene/:id`) it has two views of the same scene (spec 029):
 * the scene studio (chat | preview and direct edits) and Under the hood
 * (everything above). Both read and write the same stores, so switching
 * loses nothing. Embedded in a story tab it is always Under the hood.
 */

import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSceneStore, useSceneUndo, useSceneRedo, useCanUndo, useCanRedo, cacheDraft, clearDraft } from '../stores/sceneStore';
import { useDevicePreview, useDeviceSceneSync, type DevicePreviewContextType } from '../contexts/DevicePreviewContext';
import { Spinner } from 'react-bootstrap';
import { BsPlayFill, BsStopFill } from 'react-icons/bs';
import { useModalContext } from '../contexts/ModalContext';
import DeviceConnectModal from '../modals/DeviceConnectModal';
import ScenePublishControl from '../components/ScenePublishControl';
import SceneCanvas from '../components/motion/canvas/SceneCanvas';
import ScenePreview from '../components/preview/ScenePreview';
import PlayFromStartIcon from '../components/preview/PlayFromStartIcon';
import { inertWhen } from '../utils/inert';
import TimelinePanel from '../components/motion/timeline/TimelinePanel';
import PropertiesPanel from '../components/motion/properties/PropertiesPanel';
import AssistantPanel from '../components/motion/assistant/AssistantPanel';
import { hoodAssistantView, useAssistantView } from '../components/assistant/assistantView';
import { sceneBriefKey, useAssistantStore } from '../stores/assistantStore';
import SceneStudio from '../components/studio/SceneStudio';
import PicturesView from '../components/pictures/PicturesView';
import { ScenePictureOwnerProvider } from '../components/pictures/pictureOwner';
import ImageGenExtensions from '../components/imagegen/ImageGenExtensions';
import { isSceneView, resolveSceneView, sessionUserId, writeStoredSceneView, type SceneView } from '../utils/sceneView';
import ChoiceEditor from '../components/motion/choices/ChoiceEditor';
import SceneNameInput from '../components/motion/SceneNameInput';
import { useSceneShortcuts, isTextField } from '../hooks/useSceneShortcuts';
import { isSpriteOffCanvas } from '../utils/spriteGeometry';
import OrientationButtons from '../components/preview/OrientationButtons';
import importedStyles from './SceneEditor.module.scss';

// Provide empty object fallback if styles failed to load
const styles = importedStyles || {} as Record<string, string>;

// =============================================================================
// TEXT-FIELD UNDO GROUPS (shortcuts live in hooks/useSceneShortcuts)
// =============================================================================

/**
 * Typing in one field is one undo step (spec 018): consecutive input events
 * in the same field are grouped until it loses focus or 1 s passes without
 * input. Listens in the capture phase so the group opens before React applies
 * the first keystroke to the store.
 */
export const TEXT_EDIT_COALESCE_MS = 1000;

function useTextFieldHistoryGroups() {
  useEffect(() => {
    const { beginHistoryGroup, endHistoryGroup } = useSceneStore.getState();
    let field: EventTarget | null = null;
    let idle: ReturnType<typeof setTimeout> | null = null;

    const close = () => {
      if (idle) clearTimeout(idle);
      idle = null;
      if (field) {
        field = null;
        endHistoryGroup();
      }
    };
    const onInput = (e: Event) => {
      if (!isTextField(e.target)) return;
      if (field !== e.target) {
        close();
        field = e.target;
        beginHistoryGroup();
      }
      if (idle) clearTimeout(idle);
      idle = setTimeout(close, TEXT_EDIT_COALESCE_MS);
    };
    const onFocusOut = (e: FocusEvent) => {
      if (e.target === field) close();
    };

    window.addEventListener('input', onInput, true);
    window.addEventListener('focusout', onFocusOut, true);
    return () => {
      window.removeEventListener('input', onInput, true);
      window.removeEventListener('focusout', onFocusOut, true);
      close();
    };
  }, []);
}

// =============================================================================
// SAVE STATUS (spec 004) — autosave itself lives in sceneStore
// =============================================================================

const SaveStatusIndicator: React.FC = () => {
  const saveStatus = useSceneStore((state) => state.saveStatus);
  const saveError = useSceneStore((state) => state.saveError);
  const saveScene = useSceneStore((state) => state.saveScene);
  if (saveStatus === 'error') {
    return (
      <span className={`${styles.saveStatus} ${styles.saveStatusError}`} role="status" data-testid="save-status" title={saveError ?? undefined}>
        Save failed — <button type="button" className={styles.saveRetry} onClick={() => void saveScene()}>Retry</button>
      </span>
    );
  }
  const label = saveStatus === 'saving' ? 'Saving…' : saveStatus === 'dirty' ? 'Unsaved changes' : 'Saved';
  return (
    <span className={styles.saveStatus} role="status" data-testid="save-status">
      {saveStatus === 'dirty' && <span className={styles.unsavedIndicator}>●</span>}
      {label}
    </span>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

interface SceneEditorProps {
  /** When provided, overrides useParams for the scene ID (embedded mode). */
  sceneIdProp?: number;
  /** Called instead of navigate-back when embedded in StoryEditor tabs. */
  onClose?: () => void;
  /** When true, renders a slim bar instead of the full toolbar. */
  isEmbedded?: boolean;
}

/**
 * The header's device-preview state (spec 013): "📡 Device" (idle), a spinner
 * (creating / waiting for a device), "📡 N" with a green dot (connected), a
 * red dot with the message as tooltip (error).
 */
const DeviceButton: React.FC<{ device: DevicePreviewContextType; onClick: () => void; compact?: boolean }> = ({ device, onClick, compact }) => {
  const { status, playerCount, message } = device;
  const title =
    status === 'connected' ? `${playerCount} device${playerCount === 1 ? '' : 's'} connected — click to manage`
      : status === 'creating' ? 'Creating a device session…'
        : status === 'waiting' ? 'Waiting for a device… — click to show the QR code'
          : status === 'error' ? (message ?? 'Device preview failed')
            : message ?? 'Connect a device for remote preview';
  return (
    <button
      className={`${styles.toolbarButton} ${styles.deviceButton} ${status === 'connected' ? styles.deviceConnected : ''}`}
      onClick={onClick}
      title={title}
      data-testid="device-button"
      data-status={status}
    >
      {status === 'connected' && (
        <span className={styles.deviceConnectedLabel}>
          <span className={styles.deviceDot} data-testid="device-dot" data-dot="green" />
          📡 {playerCount}
        </span>
      )}
      {(status === 'creating' || status === 'waiting') && (
        <span className={styles.deviceConnectedLabel}>
          <Spinner animation="border" size="sm" data-testid="device-spinner" />
          {compact ? '' : ' 📡'}
        </span>
      )}
      {status === 'error' && (
        <span className={styles.deviceConnectedLabel}>
          <span className={`${styles.deviceDot} ${styles.deviceDotError}`} data-testid="device-dot" data-dot="red" />
          📡
        </span>
      )}
      {status === 'idle' && (compact ? '📡' : '📡 Device')}
    </button>
  );
};

export const SceneEditor: React.FC<SceneEditorProps> = ({ sceneIdProp, onClose, isEmbedded = false }) => {
  const { sceneId: sceneIdParam } = useParams<{ sceneId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Resolve the scene id: prop wins over route param
  const sceneId = sceneIdProp != null ? String(sceneIdProp) : sceneIdParam;

  // ── The view (spec 029): a pending brief, then `?view=`, then the
  // remembered view, then the studio. Known on the first render, so no view
  // flashes up. An embedded editor has no views.
  const queryView = new URLSearchParams(location.search).get('view');
  const [userId] = useState(sessionUserId);
  const [view, setView] = useState<SceneView>(() => (isEmbedded ? 'hood' : resolveSceneView({
    query: queryView,
    userId,
    pendingBrief: sceneId !== undefined && useAssistantStore.getState().pendingBrief[sceneBriefKey(sceneId)] !== undefined,
  })));
  const isStudio = !isEmbedded && view === 'studio';
  /** The scene's own Pictures (spec 030, 029 D-12). */
  const isPictures = !isEmbedded && view === 'pictures';
  const isHood = !isStudio && !isPictures;
  // Spec 031 v3: under the hood the assistant sees the orientation switch, the
  // playhead and the selection, read at Send (D-17).
  useAssistantView(isHood, () => hoodAssistantView(useSceneStore.getState()));
  /** The `view` query this editor last saw or wrote; only a change from outside switches. */
  const lastQuery = useRef(queryView);
  const replaceViewQuery = useCallback((next: SceneView) => {
    const params = new URLSearchParams(location.search);
    if (params.get('view') === next) return;
    params.set('view', next);
    // The Pictures view's own keys go with it.
    if (next !== 'pictures') for (const k of ['by', 'filter', 'slot']) params.delete(k);
    lastQuery.current = next;
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true, state: location.state });
  }, [location.pathname, location.search, location.state, navigate]);
  /** Lossless: both views use the same stores; ▶ Preview stops on leaving the hood. */
  const changeView = useCallback((next: SceneView, opts: { remember: boolean }) => {
    if (isEmbedded) return;
    if (useSceneStore.getState().isPlaying) useSceneStore.getState().stop();
    setView(next);
    if (opts.remember) writeStoredSceneView(userId, next);
    replaceViewQuery(next);
  }, [isEmbedded, userId, replaceViewQuery]);
  const chooseView = useCallback((next: SceneView) => changeView(next, { remember: true }), [changeView]);
  const openHood = useCallback(() => chooseView('hood'), [chooseView]);
  // The URL's `view` query, changed from outside (back/forward, a link), switches the view.
  useEffect(() => {
    if (isEmbedded || queryView === lastQuery.current) return;
    lastQuery.current = queryView;
    if (isSceneView(queryView)) changeView(queryView, { remember: false });
    // Only a change of the query itself drives this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryView]);
  // A pending brief opened the studio over `?view=hood`: the URL says so too.
  useEffect(() => {
    if (!isEmbedded && isSceneView(queryView) && queryView !== view) replaceViewQuery(view);
    // On mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Back navigation function that uses location state if available, otherwise goes back in history
  const leaveEditor = useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }
    const from = (location.state as any)?.from;
    if (from) {
      navigate(from);
    } else {
      navigate(-1);
    }
  }, [onClose, navigate, location.state]);

  // Back with unsaved changes waits for the save; if it fails the author
  // chooses between staying (default) and leaving without saving (spec 004).
  const [leaveBlocked, setLeaveBlocked] = useState(false);
  const handleBackNavigation = useCallback(async () => {
    const st = useSceneStore.getState();
    if (st.scene && (st.isDirty || st.saveStatus === 'saving')) {
      await st.saveScene();
      const after = useSceneStore.getState();
      if (after.isDirty && after.saveStatus === 'error') {
        setLeaveBlocked(true);
        return;
      }
    }
    leaveEditor();
  }, [leaveEditor]);

  const leaveWithoutSaving = useCallback(() => {
    const st = useSceneStore.getState();
    if (st.scene) clearDraft(st.scene.id);
    st.markClean();
    setLeaveBlocked(false);
    leaveEditor();
  }, [leaveEditor]);

  // Timeline resize state - use store for persistence
  const timelineHeight = useSceneStore((state) => state.timelineHeight);
  const setTimelineHeight = useSceneStore((state) => state.setTimelineHeight);
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartY = useRef(0);
  const resizeStartHeight = useRef(200);

  // Store state
  const scene = useSceneStore((state) => state.scene);
  const error = useSceneStore((state) => state.error);
  const errorNotFound = useSceneStore((state) => state.errorNotFound);
  const isDirty = useSceneStore((state) => state.isDirty);
  const saveStatus = useSceneStore((state) => state.saveStatus);
  const previewOrientation = useSceneStore((state) => state.previewOrientation);
  // The right panel's tab is editor UI state, not document (spec 022).
  const rightPanelTab = useAssistantStore((state) => state.tab);
  const setRightPanelTab = useAssistantStore((state) => state.setTab);
  const isPlaying = useSceneStore((state) => state.isPlaying);
  // Editing is off while ▶ Preview plays (spec 009)
  const previewLock = inertWhen(isPlaying);
  
  // Device preview
  const devicePreview = useDevicePreview();
  const { addModal, removeModal } = useModalContext();

  // Store actions
  const loadScene = useSceneStore((state) => state.loadScene);
  const saveScene = useSceneStore((state) => state.saveScene);
  const setPreviewOrientation = useSceneStore((state) => state.setPreviewOrientation);
  const addSprite = useSceneStore((state) => state.addSprite);
  const addDialog = useSceneStore((state) => state.addDialog);
  const addSound = useSceneStore((state) => state.addSound);
  const selectSingle = useSceneStore((state) => state.selectSingle);
  /**
   * Elements-panel selection (spec 005): a click selects one element;
   * Shift/Cmd/Ctrl-click adds or removes an element of the same type (the
   * only multi-selection; the canvas selects single).
   */
  const handleElementClick = useCallback((e: React.MouseEvent, id: string, type: 'sprite' | 'dialog' | 'sound' | 'signal') => {
    const { selection, addToSelection, removeFromSelection } = useSceneStore.getState();
    if ((e.shiftKey || e.metaKey || e.ctrlKey) && selection.type === type) {
      if (selection.ids.includes(id)) {
        if (selection.ids.length > 1) removeFromSelection(id);
      } else {
        addToSelection(id);
      }
      return;
    }
    selectSingle(id, type);
  }, [selectSingle]);
  const deleteSprite = useSceneStore((state) => state.deleteSprite);
  const deleteDialog = useSceneStore((state) => state.deleteDialog);
  const deleteSound = useSceneStore((state) => state.deleteSound);
  const deleteSignal = useSceneStore((state) => state.deleteSignal);
  const selection = useSceneStore((state) => state.selection);
  const play = useSceneStore((state) => state.play);
  const playFromStart = useSceneStore((state) => state.playFromStart);
  const stop = useSceneStore((state) => state.stop);
  const setFocusedPane = useSceneStore((state) => state.setFocusedPane);

  // Undo/redo
  const undo = useSceneUndo();
  const redo = useSceneRedo();
  const canUndo = useCanUndo();
  const canRedo = useCanRedo();

  // Hooks: the window-wide shortcuts own canvas and timeline keys, which the studio has not (029)
  useSceneShortcuts(isHood);
  useTextFieldHistoryGroups();

  // Timeline resize handlers
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartY.current = e.clientY;
    resizeStartHeight.current = timelineHeight;
  }, [timelineHeight]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaY = resizeStartY.current - e.clientY;
      const newHeight = Math.max(100, Math.min(window.innerHeight * 0.6, resizeStartHeight.current + deltaY));
      setTimelineHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, setTimelineHeight]);

  // Focus handlers for panes
  const handleCanvasFocus = useCallback(() => setFocusedPane('canvas'), [setFocusedPane]);
  const handleTimelineFocus = useCallback(() => setFocusedPane('timeline'), [setFocusedPane]);
  const handleElementsFocus = useCallback(() => setFocusedPane('elements'), [setFocusedPane]);
  const handlePropertiesFocus = useCallback(() => setFocusedPane('properties'), [setFocusedPane]);

  // Load scene on mount
  useEffect(() => {
    if (sceneId) {
      loadScene(parseInt(sceneId, 10));
    }
  }, [sceneId, loadScene]);

  // A brief from the scene start sheet (spec 029): the scene's first turn,
  // sent once, in the studio. An embedded editor never takes one.
  const loadedSceneId = useSceneStore((state) => (state.scene && !state.isLoading ? state.scene.id : undefined));
  useEffect(() => {
    if (isEmbedded || loadedSceneId === undefined || String(loadedSceneId) !== sceneId) return;
    const assistant = useAssistantStore.getState();
    const brief = assistant.takePendingSceneBrief(sceneId);
    if (brief === undefined) return;
    setView('studio');
    replaceViewQuery('studio');
    assistant.setTab('assistant');
    void assistant.send(sceneId, brief, { fromBrief: true });
    // Runs once per loaded scene; the query writer is read as it is then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedSceneId, sceneId, isEmbedded]);

  // Leaving the editor (Back, route change, story tab close/switch) never drops
  // work: a dirty scene is cached as a draft and saved; the draft is cleared
  // when that save succeeds (spec 004). A clean scene whose UI state changed
  // sends it once (spec 018); the save above already carries it.
  useEffect(() => {
    return () => {
      // Leaving the editor ends ▶ Preview (spec 009)
      if (useSceneStore.getState().isPlaying) useSceneStore.getState().stop();
      const st = useSceneStore.getState();
      if (st.scene && st.isDirty) {
        cacheDraft();
        void st.saveScene();
      } else if (st.scene) {
        void st.syncEditorStateToServer();
      }
    };
  }, []);

  // Ask the browser to confirm closing the tab while unsaved or mid-save
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty || saveStatus === 'saving') {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, saveStatus]);
  
  // Device preview (spec 013): the modal manages the session; the sync hook
  // sends the scene on join, on Preview and on edits, never per frame.
  useDeviceSceneSync();

  const handleConnectDevice = useCallback(() => {
    addModal({
      title: 'Connect Device',
      body: <DeviceConnectModal onClose={() => removeModal()} />,
      size: 'sm',
    });
  }, [addModal, removeModal]);

  const centred: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    backgroundColor: '#0a0a0f',
    color: '#fff',
    gap: '16px',
  };
  const actionButton: React.CSSProperties = {
    padding: '8px 16px',
    backgroundColor: '#4a9eff',
    border: 'none',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '14px',
    cursor: 'pointer',
  };

  // Load failure: never leaves a previous scene on screen
  if (error) {
    return (
      <div style={centred} role="alert">
        {errorNotFound ? <p>Scene not found</p> : <p style={{ color: '#ff6b6b' }}>Error: {error}</p>}
        <div style={{ display: 'flex', gap: '8px' }}>
          {!errorNotFound && sceneId && (
            <button onClick={() => loadScene(parseInt(sceneId, 10))} style={actionButton}>
              Retry
            </button>
          )}
          <button onClick={leaveEditor} style={actionButton}>
            Back
          </button>
        </div>
      </div>
    );
  }

  // Loading (or not yet loaded)
  if (!scene) {
    return (
      <div style={centred}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(74, 158, 255, 0.2)',
          borderTopColor: '#4a9eff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p>Loading scene...</p>
      </div>
    );
  }

  const editor = (
    <div className={styles.editor}>
      {leaveBlocked && (
        <div className={styles.leaveDialogBackdrop}>
          <div className={styles.leaveDialog} role="alertdialog" aria-modal="true" aria-labelledby="leave-dialog-title">
            <h3 id="leave-dialog-title">Your changes could not be saved</h3>
            <p>Stay to retry, or leave and lose the changes made since the last save.</p>
            <div className={styles.leaveDialogActions}>
              <button type="button" onClick={leaveWithoutSaving}>Leave without saving</button>
              <button type="button" autoFocus className={styles.leaveDialogPrimary} onClick={() => setLeaveBlocked(false)}>Stay</button>
            </div>
          </div>
        </div>
      )}
      {/* Full toolbar — standalone mode only */}
      {!isEmbedded && (
      <header className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <button
            className={styles.backButton}
            onClick={handleBackNavigation}
            title="Back to Previous Page"
          >
            ←
          </button>

          <div className={styles.sceneName}>
            <SceneNameInput className={styles.nameInput} data-testid="scene-name-input" />
            <SaveStatusIndicator />
          </div>
        </div>

        <div className={styles.toolbarCenter}>
          {/* The view switch (spec 029) */}
          <div className={styles.viewSwitch} role="group" aria-label="View" data-testid="scene-view-switch">
            <button
              type="button"
              className={`${styles.viewSwitchButton} ${view === 'studio' ? styles.viewSwitchOn : ''}`}
              aria-pressed={view === 'studio'}
              onClick={() => chooseView('studio')}
              data-testid="scene-view-studio"
            >
              Scene studio
            </button>
            <button
              type="button"
              className={`${styles.viewSwitchButton} ${view === 'pictures' ? styles.viewSwitchOn : ''}`}
              aria-pressed={view === 'pictures'}
              onClick={() => changeView('pictures', { remember: false })}
              data-testid="scene-view-pictures"
            >
              Pictures
            </button>
            <button
              type="button"
              className={`${styles.viewSwitchButton} ${view === 'hood' ? styles.viewSwitchOn : ''}`}
              aria-pressed={view === 'hood'}
              onClick={() => chooseView('hood')}
              data-testid="scene-view-hood"
            >
              Under the hood
            </button>
          </div>
        </div>

        <div className={styles.toolbarRight}>
          {/* Undo/Redo */}
          <div className={styles.undoRedo}>
            <button
              className={styles.toolbarButton}
              onClick={() => undo()}
              disabled={!canUndo || isPlaying}
              title="Undo (Cmd+Z)"
              aria-label="Undo"
            >
              ↩
            </button>
            <button
              className={styles.toolbarButton}
              onClick={() => redo()}
              disabled={!canRedo || isPlaying}
              title="Redo (Cmd+Shift+Z)"
              aria-label="Redo"
            >
              ↪
            </button>
          </div>

          {/* Orientation toggle (the studio's preview has its own) */}
          {isHood && (
            <OrientationButtons className={styles.orientationToggle} orientation={previewOrientation} onChange={setPreviewOrientation} testIdPrefix="scene-orientation" />
          )}

          {/* Device connect / toggle */}
          <div className={styles.deviceGroup}>
            <DeviceButton device={devicePreview} onClick={handleConnectDevice} compact={false} />
            {devicePreview.isConnected && (
              <div className={styles.playbackTarget}>
                <button
                  className={`${styles.targetButton} ${!devicePreview.previewOnDevice ? styles.targetActive : ''}`}
                  onClick={() => devicePreview.setPreviewOnDevice(false)}
                  title="Preview locally in editor"
                >
                  Local
                </button>
                <button
                  className={`${styles.targetButton} ${devicePreview.previewOnDevice ? styles.targetActive : ''}`}
                  onClick={() => devicePreview.setPreviewOnDevice(true)}
                  title="Preview on connected device"
                >
                  Remote
                </button>
              </div>
            )}
          </div>

          {/* Preview (the studio's preview replaces it) */}
          {isHood && (<>
          <button
            className={styles.toolbarButton}
            onClick={playFromStart}
            title="Preview from the start (Shift+Space)"
            aria-label="Preview from start"
            data-testid="preview-from-start"
          >
            <PlayFromStartIcon />
          </button>
          <button
            className={`${styles.toolbarButton} ${styles.previewButton}`}
            onClick={() => isPlaying ? stop() : play()}
            title={isPlaying ? 'Stop (Space)' : 'Preview from the playhead (Space)'}
            aria-label={isPlaying ? 'Stop' : 'Preview'}
            data-testid="preview-play"
          >
            {isPlaying ? <BsStopFill size={18} /> : <BsPlayFill size={20} />}
          </button>
          </>)}

          {/* Publish (spec 011): the scene as a playable item of its own */}
          {sceneId && (
            <ScenePublishControl sceneId={parseInt(sceneId, 10)} buttonClassName={styles.toolbarButton} disabled={isPlaying} />
          )}

          {/* Save */}
          <button
            className={`${styles.toolbarButton} ${styles.saveButton}`}
            onClick={() => void saveScene()}
            disabled={!isDirty}
          >
            Save
          </button>
        </div>
      </header>
      )}

      {/* Slim embedded bar — name + controls */}
      {isEmbedded && (
        <div className={styles.embeddedBar}>
          <span className={styles.embeddedSceneName}>
            <SceneNameInput className={styles.embeddedNameInput} data-testid="scene-name-input" />
            <SaveStatusIndicator />
          </span>
          <div className={styles.embeddedControls}>
            <div className={styles.undoRedo}>
              <button
                className={styles.toolbarButton}
                onClick={() => undo()}
                disabled={!canUndo || isPlaying}
                title="Undo (Cmd+Z)"
                aria-label="Undo"
              >
                ↩
              </button>
              <button
                className={styles.toolbarButton}
                onClick={() => redo()}
                disabled={!canRedo || isPlaying}
                title="Redo (Cmd+Shift+Z)"
                aria-label="Redo"
              >
                ↪
              </button>
            </div>
            <OrientationButtons className={styles.orientationToggle} orientation={previewOrientation} onChange={setPreviewOrientation} />
            {/* Device connect / toggle (embedded) */}
            <div className={styles.deviceGroup}>
              <DeviceButton device={devicePreview} onClick={handleConnectDevice} compact={true} />
              {devicePreview.isConnected && (
                <div className={styles.playbackTarget}>
                  <button
                    className={`${styles.targetButton} ${!devicePreview.previewOnDevice ? styles.targetActive : ''}`}
                    onClick={() => devicePreview.setPreviewOnDevice(false)}
                    title="Preview locally in editor"
                  >
                    Local
                  </button>
                  <button
                    className={`${styles.targetButton} ${devicePreview.previewOnDevice ? styles.targetActive : ''}`}
                    onClick={() => devicePreview.setPreviewOnDevice(true)}
                    title="Preview on connected device"
                  >
                    Remote
                  </button>
                </div>
              )}
            </div>
            {/* Preview */}
            <button
              className={styles.toolbarButton}
              onClick={playFromStart}
              title="Preview from the start (Shift+Space)"
              aria-label="Preview from start"
              data-testid="preview-from-start"
            >
              <PlayFromStartIcon />
            </button>
            <button
              className={`${styles.toolbarButton} ${styles.previewButton}`}
              onClick={() => isPlaying ? stop() : play()}
              title={isPlaying ? 'Stop (Space)' : 'Preview from the playhead (Space)'}
              aria-label={isPlaying ? 'Stop' : 'Preview'}
              data-testid="preview-play"
            >
              {isPlaying ? <BsStopFill size={18} /> : <BsPlayFill size={20} />}
            </button>
            <button
              className={`${styles.toolbarButton} ${styles.saveButton}`}
              onClick={() => void saveScene()}
              disabled={!isDirty}
            >
              Save
            </button>
          </div>
        </div>
      )}

      {isStudio && sceneId ? (
        <SceneStudio sceneId={parseInt(sceneId, 10)} onOpenHood={openHood} />
      ) : isPictures && sceneId ? (
        // Spec 026 v15: a flex column, not a scroller; the view's own panes scroll.
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }} data-testid="scene-pictures">
          {scene ? <PicturesView /> : null}
        </div>
      ) : (<>
      {/* Main content area */}
      <div className={styles.mainContent}>
        {/* Left panel - Element browser */}
        <aside className={styles.leftPanel} onClick={handleElementsFocus} onFocus={handleElementsFocus} {...previewLock}>
          <div className={styles.panelHeader}>
            <h3>Elements</h3>
          </div>
          <div className={styles.panelContent}>
            {/* Sprites Section */}
            <div className={styles.elementSection}>
              <div className={styles.sectionHeader}>
                <h4>Sprites</h4>
                <button 
                  className={styles.addButton}
                  onClick={() => addSprite()}
                  title="Add Sprite"
                >
                  +
                </button>
              </div>
              <div className={styles.elementList}>
                {scene?.sprites.map((sprite) => (
                  <div 
                    key={sprite.id}
                    className={`${styles.elementItem} ${selection.ids.includes(sprite.id) ? styles.selected : ''}`}
                    data-element-id={sprite.id}
                    onClick={(e) => handleElementClick(e, sprite.id, 'sprite')}
                  >
                    <span className={styles.elementIcon}>🖼️</span>
                    <span className={styles.elementName}>{sprite.name}</span>
                    {isSpriteOffCanvas(sprite, previewOrientation) && (
                      <span
                        className={styles.offCanvasMarker}
                        data-testid="off-canvas-marker"
                        title={`Off-canvas in ${previewOrientation}`}
                      >
                        off-canvas
                      </span>
                    )}
                    <button 
                      className={styles.deleteButton}
                      onClick={(e) => { e.stopPropagation(); deleteSprite(sprite.id); }}
                      title="Delete"
                    >
                      ×
                    </button>
                  </div>
                ))}
                {(!scene?.sprites || scene.sprites.length === 0) && (
                  <p className={styles.emptyMessage}>No sprites</p>
                )}
              </div>
            </div>

            {/* Dialogs Section */}
            <div className={styles.elementSection}>
              <div className={styles.sectionHeader}>
                <h4>Dialogs</h4>
                <button 
                  className={styles.addButton}
                  onClick={() => addDialog()}
                  title="Add Dialog"
                >
                  +
                </button>
              </div>
              <div className={styles.elementList}>
                {scene?.dialogs.map((dialog, index) => (
                  <div 
                    key={dialog.id}
                    className={`${styles.elementItem} ${selection.ids.includes(dialog.id) ? styles.selected : ''}`}
                    onClick={(e) => handleElementClick(e, dialog.id, 'dialog')}
                  >
                    <span className={styles.elementIcon}>💬</span>
                    <span className={styles.elementName}>{dialog.name || `Dialog ${index + 1}`}</span>
                    <button 
                      className={styles.deleteButton}
                      onClick={(e) => { e.stopPropagation(); deleteDialog(dialog.id); }}
                      title="Delete"
                    >
                      ×
                    </button>
                  </div>
                ))}
                {(!scene?.dialogs || scene.dialogs.length === 0) && (
                  <p className={styles.emptyMessage}>No dialogs</p>
                )}
              </div>
            </div>

            {/* Sounds Section */}
            <div className={styles.elementSection}>
              <div className={styles.sectionHeader}>
                <h4>Sounds</h4>
                <button 
                  className={styles.addButton}
                  onClick={() => addSound({ type: 'sfx', mediaId: 0 })}
                  title="Add Sound"
                >
                  +
                </button>
              </div>
              <div className={styles.elementList}>
                {scene?.sounds.map((sound, index) => (
                  <div 
                    key={sound.id}
                    className={`${styles.elementItem} ${selection.ids.includes(sound.id) ? styles.selected : ''}`}
                    onClick={(e) => handleElementClick(e, sound.id, 'sound')}
                  >
                    <span className={styles.elementIcon}>{sound.type === 'bgm' ? '🎵' : sound.type === 'voice' ? '🎤' : '🔊'}</span>
                    <span className={styles.elementName}>{sound.name || `Sound ${index + 1}`}</span>
                    {!(sound.mediaId > 0) && (
                      <span className={styles.offCanvasMarker} data-testid="sound-no-audio-marker" title="This sound has no audio file and plays nothing">
                        No audio file
                      </span>
                    )}
                    <button 
                      className={styles.deleteButton}
                      onClick={(e) => { e.stopPropagation(); deleteSound(sound.id); }}
                      title="Delete"
                    >
                      ×
                    </button>
                  </div>
                ))}
                {(!scene?.sounds || scene.sounds.length === 0) && (
                  <p className={styles.emptyMessage}>No sounds</p>
                )}
              </div>
            </div>

            {/* Signals Section (spec 007): existing signals only, never authored here */}
            {(scene?.signals?.length ?? 0) > 0 && (
              <div className={styles.elementSection} data-testid="signals-section">
                <div className={styles.sectionHeader}>
                  <h4>Signals</h4>
                </div>
                <div className={styles.elementList}>
                  {scene!.signals.map((signal) => (
                    <div
                      key={signal.id}
                      className={`${styles.elementItem} ${selection.ids.includes(signal.id) ? styles.selected : ''}`}
                      data-element-id={signal.id}
                      onClick={(e) => handleElementClick(e, signal.id, 'signal')}
                    >
                      <span className={styles.elementIcon}>⚡</span>
                      <span className={styles.elementName}>
                        {signal.type}{typeof signal.trigger?.time === 'number' ? ` @ ${(signal.trigger.time / 1000).toFixed(1)}s` : ''}
                      </span>
                      <button
                        className={styles.deleteButton}
                        onClick={(e) => { e.stopPropagation(); deleteSignal(signal.id); }}
                        title="Delete"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Choices Section */}
            <div className={styles.elementSection}>
              <div className={styles.sectionHeader}>
                <h4>Choices</h4>
              </div>
              <ChoiceEditor />
            </div>
          </div>
        </aside>

        {/* Center - Canvas */}
        <main className={styles.canvasArea} onClick={handleCanvasFocus} onFocus={handleCanvasFocus}>
          <SceneCanvas />
          {/* ▶ Preview: the canonical player over the canvas (spec 009) */}
          <ScenePreview />
        </main>

        {/* Right panel - Properties */}
        <aside className={styles.rightPanel} onClick={handlePropertiesFocus} onFocus={handlePropertiesFocus} {...previewLock}>
          <div className={styles.panelTabs} role="tablist" aria-label="Right panel">
            <button
              type="button"
              role="tab"
              aria-selected={rightPanelTab === 'properties'}
              className={`${styles.panelTab} ${rightPanelTab === 'properties' ? styles.active : ''}`}
              onClick={() => setRightPanelTab('properties')}
              data-testid="right-tab-properties"
            >
              Properties
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={rightPanelTab === 'assistant'}
              className={`${styles.panelTab} ${rightPanelTab === 'assistant' ? styles.active : ''}`}
              onClick={() => setRightPanelTab('assistant')}
              data-testid="right-tab-assistant"
            >
              Assistant
            </button>
          </div>
          {rightPanelTab === 'assistant' && sceneId ? (
            <AssistantPanel sceneId={sceneId} />
          ) : (
            <>
              <div className={styles.panelHeader}>
                <h3>{selection.type === 'sprite' ? 'Sprite' : selection.type === 'dialog' ? 'Dialog' : selection.type === 'sound' ? 'Sound' : selection.type === 'signal' ? 'Signal' : 'Scene'}</h3>
              </div>
              <div className={styles.panelContent}>
                <PropertiesPanel />
              </div>
            </>
          )}
        </aside>
      </div>

      {/* Bottom panel: always the timeline (spec 006 v5) */}
        <div className={styles.timelineArea} style={{ height: timelineHeight }} onClick={handleTimelineFocus} onFocus={handleTimelineFocus}>
          <div 
            className={`${styles.resizeHandle} ${isResizing ? styles.dragging : ''}`}
            onMouseDown={handleResizeStart}
          />
          <TimelinePanel />
        </div>
      </>)}


      {/* Device preview indicator */}
      {isPlaying && devicePreview.isConnected && devicePreview.previewOnDevice && (
        <div className={styles.devicePreviewIndicator}>
          <div className={styles.devicePreviewContent}>
            <span className={styles.deviceIcon}>📱</span>
            <span className={styles.deviceText}>Playing on device</span>
            <button className={styles.stopButton} onClick={stop}>
              ⏹ Stop
            </button>
          </div>
        </div>
      )}
    </div>
  );
  // Spec 030: a standalone scene has its own pictures and look; an embedded editor uses its story's.
  if (isEmbedded) return editor;
  return (
    <ScenePictureOwnerProvider>
      <ImageGenExtensions>{editor}</ImageGenExtensions>
    </ScenePictureOwnerProvider>
  );
};

export default SceneEditor;
